/**
 * Booking Link  —  /booking-link
 *
 * Two jobs on one page:
 *
 *   1. Pick a mediator and see (and copy) their public booking link. The picker
 *      is the same control the Schedule page uses, down to the localStorage key
 *      pattern, so moving between the two screens feels like one product.
 *
 *   2. Build an availability report across SEVERAL mediators at once. The
 *      Schedule page already reports on a single mediator; case managers who
 *      look after ten or twenty of them were running that once per mediator and
 *      stitching the output together by hand.
 *
 * Schedule.jsx is deliberately NOT touched. Its single-mediator report is in
 * daily use, so this page re-implements the roll-up against the same
 * GET /schedule endpoint rather than refactoring a shared helper out from under
 * it. The duplication is the price of leaving a working screen alone.
 *
 * The output format matches Schedule.jsx — same header line, same (M)/(A)
 * annotations, same comma-separated days — with ONE deliberate difference: this
 * report lists a month with no open dates as "None", where Schedule
 * omits it entirely. Omitting it makes a booked-solid month indistinguishable
 * from one that was never in range, which is the whole reason this version
 * exists. So the two screens can legitimately print different text for the same
 * mediator, and that is the intended behaviour, not drift.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bookingUrlForUserId } from '../utils/scalePortalUrl';
import api from '../utils/api';
import { FiCopy } from 'react-icons/fi';

/* A <select>'s open list is drawn by the OS and cannot be styled, so on macOS
 * it appeared as a dark native panel in the middle of a white modal. This is
 * the same button-plus-positioned-panel pattern PaymentActionMenu uses, so the
 * open list matches everything else in the portal. Positioned `fixed` off the
 * trigger's rect rather than absolutely, so the modal's own overflow cannot
 * clip it. */
function MonthsSelect({ value, onChange, options, id }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    // Flip above the trigger when there is not room below for the panel.
    const height = Math.min(options.length * 34 + 8, 280);
    const below = window.innerHeight - rect.bottom - 8;
    setPos({
      top: below >= height ? rect.bottom + 4 : Math.max(8, rect.top - height - 4),
      left: rect.left,
      width: Math.max(rect.width, 150),
      maxHeight: height,
    });
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <>
      <button
        id={id}
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex w-[150px] items-center justify-between gap-2 rounded-md border bg-white px-2.5 py-1.5 text-sm text-[#333333] ${open ? 'border-[#115e59]' : 'border-[#d1d5db]'}`}
      >
        <span>{selected ? selected.label : ''}</span>
        <svg className="h-4 w-4 shrink-0 text-[#9ca3af]" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
          <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && pos && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
          <div
            role="listbox"
            style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
            className="fixed z-[9999] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          >
            {options.map((o) => {
              const isSelected = o.value === value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => { onChange(o.value); setOpen(false); }}
                  className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-gray-100 ${isSelected ? 'font-medium text-[#115e59]' : 'text-[#333333]'}`}
                >
                  <span>{o.label}</span>
                  {isSelected && (
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 10.5l3.5 3.5L15 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const DOW_TO_DAY = { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday' };
const MEDIATOR_KEY = 'cm_booking_link_selected_mediator';

/** Run `task` over `items` with at most `limit` in flight.
 *
 *  The report used to await every request one after another: mediators in an
 *  outer loop, months in an inner one. Four mediators over two months was eight
 *  serial round trips, and each /schedule call runs three queries of its own, so
 *  the wait was the SUM of all of them. Running them together makes it the
 *  slowest one instead.
 *
 *  Capped rather than unbounded: twelve months across twenty mediators is 240
 *  requests, and firing those at once would bury the backend and trip rate
 *  limits. Results come back in the original order regardless of finish order,
 *  which the report relies on to keep mediators in the listed sequence.
 *
 *  NOTE the cap is per call and these NEST — generateReport runs mediators
 *  through one of these, and each mediator's months through another — so peak
 *  in-flight requests is limit x limit (36), not limit. The browser's own
 *  per-origin connection cap keeps the wire quieter than that, but 36 is the
 *  number the backend can see. */
const mapWithLimit = async (items, limit, task) => {
  const results = new Array(items.length);
  let cursor = 0;
  const worker = async () => {
    for (;;) {
      const i = cursor;
      cursor += 1;
      if (i >= items.length) return;
      results[i] = await task(items[i], i);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker()),
  );
  return results;
};

/** Requests in flight at once. Six keeps a twelve-month, twenty-mediator report
 *  moving without swamping a backend that answers in roughly two seconds. */
const REQUEST_CONCURRENCY = 6;

/** The public page a party books on.
 *
 *  Delegates to the shared resolver rather than reading the env var directly.
 *  Reading it directly is what broke this page on UAT: CRA inlines REACT_APP_*
 *  at build time and the variable is not set in the deployed Vercel project, so
 *  the origin came out empty for every mediator. The resolver falls back to the
 *  CM host, which is how the mediator-detail Booking Link tab has always worked.
 *
 *  Keyed strictly on userId — see bookingUrlForUserId for why a numeric-id
 *  fallback would be worse than no link at all. */
const bookingUrlFor = (mediator) => bookingUrlForUserId(mediator?.userId);

const BookingLink = () => {
  const [mediators, setMediators] = useState([]);
  const [selectedMediator, setSelectedMediator] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);
  // Remount key for the preview frame: changing it forces a reload when the
  // mediator changes, which an src swap alone does not reliably do in every
  // browser once the frame has navigated internally through the booking steps.
  const [previewNonce, setPreviewNonce] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);

  // --- multi-mediator report state (independent of the Schedule page) ---
  const [reportIds, setReportIds] = useState([]);           // mediator ids chosen for the report
  const [monthsAhead, setMonthsAhead] = useState(1);
  const [selectedDays, setSelectedDays] = useState({
    Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true,
  });
  /* Dates on hold are EXCLUDED by default.
   *
   * A held slot keeps status 'available' — the hold is a separate flag — so the
   * report used to list it as free. On UAT that is 33 slots, several held for a
   * named firm (Littler, Shegerian and Associates), and offering one of those to
   * a second firm is the expensive direction to be wrong in. Off by default,
   * opt in when you want to see what might free up. */
  /* Same three-checkbox model the Scale portal's report uses, with the same
   * semantics, so the two reports cannot disagree: Morning and Afternoon each
   * admit that half when it is free, and Full day additionally admits a date
   * whose BOTH halves are free even when the half-day boxes are off. That is
   * why it is a third checkbox rather than a stricter mode. */
  const [selectedSlots, setSelectedSlots] = useState({
    morning: true, afternoon: true, fullday: true,
  });
  const [includeOnHold, setIncludeOnHold] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [progress, setProgress] = useState('');
  const reportRef = useRef(null);

  useEffect(() => {
    api.get('/mediators?limit=200')
      .then((res) => {
        const list = (res.data?.data || []).filter((m) => m.name && m.name.trim());
        setMediators(list);
        if (list.length && !selectedMediator) {
          let saved = null;
          try { saved = localStorage.getItem(MEDIATOR_KEY); } catch (_e) { /* storage unavailable */ }
          const match = saved ? list.find((m) => String(m.id) === saved) : null;
          setSelectedMediator(match || list[0]);
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esc closes the report, which anyone who has used a dialog will try first.
  useEffect(() => {
    if (!reportOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setReportOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reportOpen]);

  /* Freeze the page behind the dialog while it is open.
   *
   * Layout is `min-h-screen`, so the Booking Link page scrolls the DOCUMENT
   * rather than an inner container — once the pointer left the overlay the
   * wheel fell through and the calendar drifted around behind the modal. The
   * overlay keeps its own `overflow-y-auto`, so a report taller than the
   * viewport still scrolls; it is only the page underneath that is pinned.
   *
   * The padding swap compensates for the scrollbar that hiding the overflow
   * removes — without it the header and sidebar jump sideways by its width the
   * moment the modal opens. Both values are captured and restored rather than
   * reset to '', so nothing else that styles the body gets clobbered.
   */
  useEffect(() => {
    if (!reportOpen) return undefined;
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [reportOpen]);

  const chooseMediator = (m) => {
    setSelectedMediator(m);
    setPickerOpen(false);
    setSearch('');
    setCopied(false);
    setPreviewNonce((n) => n + 1);
    try { localStorage.setItem(MEDIATOR_KEY, String(m.id)); } catch (_e) { /* storage unavailable */ }
  };

  const bookingUrl = bookingUrlFor(selectedMediator);

  const copyLink = async () => {
    if (!bookingUrl) return;
    try {
      await navigator.clipboard.writeText(bookingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setFeedback({ type: 'info', text: 'Could not copy automatically — select the link and copy it.' });
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? mediators.filter((m) => m.name.toLowerCase().includes(q)) : mediators;
  }, [mediators, search]);

  const toggleReportId = (id) => {
    setReportIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  /** Pull every slot in the window for ONE mediator and roll it up per date.
   *  Same shape the Schedule page produces, so the text output matches. */
  const collectFor = useCallback(async (mediator, months, startKey, endKey) => {
    const id = mediator.userId;
    // Every month for this mediator at once; the responses are independent.
    const perMonth = await mapWithLimit(months, REQUEST_CONCURRENCY, ({ year, month }) =>
      api.get(`/schedule?mediatorId=${id}&month=${month}&year=${year}`),
    );
    const rows = [];
    perMonth.forEach((res) => {
      (res?.data?.data || []).forEach((r) => rows.push(r));
    });
    const byDate = {};
    rows.forEach((r) => {
      if (String(r.status || '').trim().toLowerCase() !== 'available') return;
      // A hold does not change `status`, so without this the slot reads as free.
      const held = r.on_hold === true;
      if (held && !includeOnHold) return;
      if (!r.date || r.date < startKey || r.date > endKey) return;
      const dayKey = DOW_TO_DAY[new Date(`${r.date}T00:00:00`).getDay()];
      if (!dayKey || !selectedDays[dayKey]) return;
      /* Free and held halves are tracked SEPARATELY, not as one flag on the
       * date. A single day is routinely split — Oct 2 has a held morning and a
       * free afternoon — and the report has to say both: `2(A)` on the
       * availability line and `2(M)` on the on-hold line. Collapsing the day to
       * one held/not-held boolean cannot express that. */
      if (!byDate[r.date]) {
        byDate[r.date] = {
          free: { morning: false, afternoon: false },
          held: { morning: false, afternoon: false },
        };
      }
      const bucket = held ? byDate[r.date].held : byDate[r.date].free;
      const period = String(r.period || '').toUpperCase();
      const slot = String(r.slot || '').toLowerCase();
      if (period === 'AM' || slot === 'morning') bucket.morning = true;
      if (period === 'PM' || slot === 'afternoon') bucket.afternoon = true;
      if (slot === 'fullday') { bucket.morning = true; bucket.afternoon = true; }
    });
    /* Seed EVERY month in the requested window, in order, before filling any
     * dates in.
     *
     * Grouping straight off the dates only ever produced keys for months that
     * had one, so a fully booked month vanished from the report. Kevin Barnes
     * showed "March, April, June" with no hint that May was booked solid rather
     * than omitted — the reader cannot tell the difference. Seeding first means
     * an empty month is still a key, and renders as "None".
     *
     * Keyed by year-month, not month name, so a window crossing a year boundary
     * keeps December and the following January apart instead of colliding. */
    const groups = {};
    months.forEach(({ year, month }) => {
      groups[`${year}-${String(month).padStart(2, '0')}`] = {
        label: MONTH_NAMES[month - 1],
        days: [],
        heldDays: [],
      };
    });
    Object.keys(byDate).sort().forEach((dateStr) => {
      const d = new Date(`${dateStr}T00:00:00`);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!groups[key]) return;      // outside the window; the filter above already excluded these
      const raw = byDate[dateStr];
      /* Port of buildAppointmentReportRows in the portal's
       * AvailabilityReportModal — kept line-for-line equivalent on purpose. */
      const applySlots = ({ morning, afternoon }) => {
        const both = morning && afternoon;
        let m = false;
        let a = false;
        if (selectedSlots.morning && morning) m = true;
        if (selectedSlots.afternoon && afternoon) a = true;
        if (selectedSlots.fullday && both) { m = true; a = true; }
        return { morning: m, afternoon: a };
      };
      // Applied to the on-hold line too, so both lines answer the same question.
      const free = applySlots(raw.free);
      const heldHalves = applySlots(raw.held);
      // Same (M)/(A) rule on each line independently: annotate when a single
      // half is involved, bare when the whole day is.
      const annotate = ({ morning, afternoon }) =>
        morning && afternoon ? '' : afternoon ? '(A)' : morning ? '(M)' : '';
      const day = d.getDate();
      if (free.morning || free.afternoon) {
        groups[key].days.push(`${day}${annotate(free)}`);
      }
      if (heldHalves.morning || heldHalves.afternoon) {
        groups[key].heldDays.push(`${day}${annotate(heldHalves)}`);
      }
    });
    return groups;
  }, [selectedDays, includeOnHold, selectedSlots]);

  const generateReport = async () => {
    if (reportLoading) return;
    const chosen = mediators.filter((m) => reportIds.includes(m.id));
    if (!chosen.length) {
      setFeedback({ type: 'warning', text: 'Select at least one mediator.' });
      return;
    }
    if (!Object.values(selectedDays).some(Boolean)) {
      setFeedback({ type: 'warning', text: 'Select at least one weekday.' });
      return;
    }
    setReportLoading(true);
    setFeedback(null);
    setReportText('');
    try {
      const today = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const startKey = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
      const months = [];
      let y = today.getFullYear();
      let m = today.getMonth();
      for (let i = 0; i <= monthsAhead; i++) {
        months.push({ year: y, month: m + 1 });
        m += 1;
        if (m > 11) { m = 0; y += 1; }
      }
      const endY = today.getFullYear() + Math.floor((today.getMonth() + monthsAhead) / 12);
      const endM = (today.getMonth() + monthsAhead) % 12;
      const endKey = `${endY}-${pad(endM + 1)}-${pad(new Date(endY, endM + 1, 0).getDate())}`;

      const sections = [];
      // Mediators run together too, so the wait is the slowest mediator rather
      // than the sum of all of them. Progress counts COMPLETIONS, because with
      // work in flight "which one are we on" is no longer a single answer.
      let done = 0;
      setProgress(`0 of ${chosen.length}`);
      /* One mediator's failure must not void everybody else's report. These
       * run under Promise.all, so an unguarded throw rejected the whole batch
       * and the user lost all of it to a single transient request — the more
       * mediators selected, the likelier that became. Each mediator is caught
       * on its own; the ones that worked still render, and the ones that did
       * not are named. */
      const failed = [];
      const collected = await mapWithLimit(chosen, REQUEST_CONCURRENCY, async (med) => {
        const name = med.name || 'Mediator';
        try {
          const groups = await collectFor(med, months, startKey, endKey);
          return { name, groups };
        } catch (err) {
          console.error(`Availability lookup failed for ${name}:`, err);
          failed.push(name);
          return null;
        } finally {
          done += 1;
          setProgress(`${done} of ${chosen.length}`);
        }
      });
      // mapWithLimit preserves input order, so the report lists mediators in the
      // order they appear in the picker no matter which finished first.
      collected.forEach((sec) => { if (sec) sections.push(sec); });
      setProgress('');

      const now = new Date();
      const h12 = now.getHours() % 12 === 0 ? 12 : now.getHours() % 12;
      const ampm = now.getHours() >= 12 ? 'PM' : 'AM';
      const stamp = `${pad(now.getMonth() + 1)}/${pad(now.getDate())}/${String(now.getFullYear()).slice(-2)} @ ${h12}:${pad(now.getMinutes())}${ampm}`;

      let out = '';
      sections.forEach((s, idx) => {
        if (idx > 0) out += '\n\n';
        out += `${s.name} availability as of ${stamp}\n\n`;
        // Keys are year-month and were seeded in window order, so every month
        // between now and the end of the range prints — a booked-solid month
        // says "None" instead of silently disappearing.
        Object.keys(s.groups).forEach((k, i) => {
          if (i > 0) out += '\n';
          const { label, days, heldDays } = s.groups[k];
          out += days.length
            ? `${label} Availability: ${days.join(', ')}\n`
            : `${label} Availability: None\n`;
          /* Holds get their OWN line, directly under the month's availability
           * line with no blank between them — the pair reads as one block.
           * This replaced an inline (H) suffix: a held date mixed into the
           * availability list still looked like something the reader could
           * book, and the day can be split (free afternoon, held morning), so
           * the same date legitimately appears on both lines with different
           * halves. */
          if (heldDays.length) out += `${label} On Hold: ${heldDays.join(', ')}\n`;
        });
      });
      const anyDates = sections.some((s) =>
        Object.values(s.groups).some((g) => g.days.length || g.heldDays.length),
      );
      // No (H) line: the "On Hold:" label names it, so the only marks left
      // to explain are the half-day ones.
      if (anyDates) out += '\n(M): Morning\n(A): Afternoon';
      setReportText(out);
      /* Name whoever could not be reached rather than reporting a clean
       * success: a report silently missing a mediator is worse than one that
       * says so, because it reads as "no availability" for that firm. */
      const missed = failed.length
        ? ` Could not reach ${failed.join(', ')} — ${failed.length === 1 ? 'that mediator is' : 'those mediators are'} not in the report.`
        : '';
      if (!sections.length) {
        setFeedback({
          type: 'error',
          text: `Could not reach any of the selected mediators${failed.length ? `: ${failed.join(', ')}` : ''}.`,
        });
      } else {
        setFeedback(
          anyDates
            ? { type: failed.length ? 'info' : 'success', text: `Report generated for ${sections.length} mediator${sections.length > 1 ? 's' : ''}.${missed}` }
            : { type: 'info', text: `No available dates found for the selected criteria.${missed}` },
        );
      }
    } catch (err) {
      console.error('Failed to generate availability report:', err);
      setFeedback({ type: 'error', text: 'Error generating availability report.' });
      setProgress('');
    } finally {
      setReportLoading(false);
    }
  };

  const copyReport = async () => {
    if (!reportText) return;
    try {
      await navigator.clipboard.writeText(reportText);
      setFeedback({ type: 'success', text: 'Report copied to clipboard!' });
    } catch {
      const ta = reportRef.current;
      if (ta) {
        ta.focus(); ta.select();
        try { document.execCommand('copy'); } catch { /* noop */ }
        setFeedback({ type: 'info', text: 'Report selected — press Cmd/Ctrl+C to copy.' });
      }
    }
  };

  const feedbackTone = {
    success: 'text-[#115e59]', error: 'text-red-600',
    warning: 'text-amber-600', info: 'text-[#666666]',
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-[#111827]">Booking Link</h1>
      <p className="mt-1 text-sm text-[#666666]">
        Share a mediator&apos;s booking page, or build an availability report across several mediators.
      </p>

      {/* ---------------- multi-mediator availability report ----------------
        * Behind a button rather than laid out on the page. The controls are
        * only wanted at the moment someone is building a report, and expanded
        * they pushed the booking link and its preview below the fold. This is
        * also the shape the mediator portal already uses for its own
        * Availability Report, so the two products behave the same way. */}
      <section className="mt-6 flex items-center justify-between rounded-lg border border-[#e5e7eb] bg-white p-5">
        <div>
          <h2 className="text-sm font-semibold text-[#111827]">Availability report</h2>
          <p className="mt-1 text-xs text-[#666666]">
            Build a shareable list of open dates across any number of mediators.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setReportOpen(true)}
          className="shrink-0 rounded-md bg-[#115e59] px-4 py-2 text-sm font-medium text-white hover:bg-[#0d4a46]"
        >
          Availability Report
        </button>
      </section>

      {reportOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Availability report"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setReportOpen(false); }}
        >
        <div className="my-auto flex max-h-[92vh] w-full max-w-[1280px] flex-col rounded-lg bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[#111827]">Availability report</h2>
            <p className="mt-1 text-xs text-[#666666]">
              Select any number of mediators. Each appears as its own block in one report.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setReportOpen(false)}
            aria-label="Close"
            className="rounded p-1 text-[#666666] hover:bg-[#f0f0f0] hover:text-[#111827]"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid gap-5 md:grid-cols-[minmax(0,280px)_1fr]">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-[#666666]">
                Mediators ({reportIds.length})
              </span>
              <div className="flex gap-2">
                <button type="button" onClick={() => setReportIds(mediators.map((m) => m.id))}
                  className="text-xs text-[#115e59] hover:underline">All</button>
                <button type="button" onClick={() => setReportIds([])}
                  className="text-xs text-[#666666] hover:underline">None</button>
              </div>
            </div>
            <div className="max-h-[340px] overflow-auto rounded-md border border-[#e5e7eb]">
              {mediators.map((m) => (
                <label key={m.id} className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-[#f7f7f7]">
                  <input
                    type="checkbox"
                    checked={reportIds.includes(m.id)}
                    onChange={() => toggleReportId(m.id)}
                    className="h-4 w-4 accent-[#115e59]"
                  />
                  <span className="truncate text-[#333333]">{m.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            {/* Laid out across the width rather than stacked down the left.
              * The panel is ~900px wide; a single column left most of it empty
              * and pushed the report box far down the modal. The month range
              * is one short row, the two checkbox groups sit side by side
              * because neither needs full width, and the on-hold toggle shares
              * its row with the action button. */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <label
                  htmlFor="report-months"
                  className="shrink-0 text-xs font-semibold uppercase tracking-wide text-[#666666]"
                >
                  Show availability for the next
                </label>
                {/* Value is months AHEAD of the current one, so n=0 is "this
                  * month only" and n=11 reaches 12 months inclusive. */}
                <MonthsSelect
                  id="report-months"
                  value={monthsAhead}
                  onChange={setMonthsAhead}
                  options={Array.from({ length: 12 }, (_, n) => ({
                    value: n,
                    label: n === 0 ? '1 month' : `${n + 1} months`,
                  }))}
                />
              </div>

              {/* Two columns on a wide modal, stacked on a narrow one. Days
                * takes the larger share: five checkboxes against three. */}
              <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,0.75fr)]">
                <div>
                  <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#666666]">
                    Days
                  </span>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    {WEEKDAYS.map((d) => (
                      <label key={d} className="flex cursor-pointer items-center gap-1.5 text-sm text-[#333333]">
                        <input
                          type="checkbox"
                          checked={!!selectedDays[d]}
                          onChange={() => setSelectedDays((p) => ({ ...p, [d]: !p[d] }))}
                          className="h-4 w-4 accent-[#115e59]"
                        />
                        {d.slice(0, 3)}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#666666]">
                    Time of day
                  </span>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    {[
                      ['morning', 'Morning'],
                      ['afternoon', 'Afternoon'],
                      ['fullday', 'Full day'],
                    ].map(([key, label]) => (
                      <label key={key} className="flex cursor-pointer items-center gap-1.5 text-sm text-[#333333]">
                        <input
                          type="checkbox"
                          checked={!!selectedSlots[key]}
                          onChange={() => setSelectedSlots((p) => ({ ...p, [key]: !p[key] }))}
                          className="h-4 w-4 accent-[#115e59]"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* The toggle and the action share a row: the toggle is the last
                * thing you decide before generating, and pairing them reclaims
                * a whole row of height. The weekday/time groups above narrow
                * WHICH slots count; this changes WHAT counts as available, so
                * it stays out of those groups. */}
              <div className="flex flex-wrap items-start justify-between gap-4 border-t border-[#f0f0f0] pt-4">
                <label className="flex max-w-[400px] cursor-pointer items-start gap-2 text-sm text-[#333333]">
                  <input
                    type="checkbox"
                    checked={includeOnHold}
                    onChange={() => setIncludeOnHold((v) => !v)}
                    className="mt-0.5 h-4 w-4 accent-[#115e59]"
                  />
                  <span>
                    Include on-hold dates
                    <span className="mt-0.5 block text-xs text-[#666666]">
                      Held for another firm. Listed on their own line per month.
                    </span>
                  </span>
                </label>
                <div className="flex shrink-0 items-center gap-3">
                  {progress && <span className="text-xs text-[#666666]">{progress}</span>}
                  {reportText && (
                    <button
                      type="button"
                      onClick={copyReport}
                      className="inline-flex items-center gap-1.5 rounded-md border border-[#ccfbf1] bg-[#f0fdfa] px-4 py-2 text-sm font-medium text-[#115e59] hover:bg-[#ccfbf1]"
                    >
                      <FiCopy className="h-4 w-4" />
                      Copy Report
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={generateReport}
                    disabled={reportLoading}
                    className="rounded-md bg-[#115e59] px-4 py-2 text-sm font-medium text-white hover:bg-[#0d4a46] disabled:opacity-50"
                  >
                    {reportLoading ? 'Generating…' : 'Generate report'}
                  </button>
                </div>
              </div>
            </div>

            {feedback && (
              <p className={`mt-3 text-sm ${feedbackTone[feedback.type] || 'text-[#666666]'}`}>{feedback.text}</p>
            )}

            {/* Stays a plain readOnly textarea on purpose: Copy sends exactly
              * these characters to the clipboard, so the box has to be the
              * deliverable rather than a prettier rendering of it. A styled
              * preview would drift from what actually lands in the email.
              *
              * Monospace, matching the Scale portal's own availability report
              * so the two previews look the same side by side. An earlier
              * revision here used a proportional face on the argument that the
              * recipient reads it in a proportional mail client; parity with
              * the portal was chosen over that. */}
            <textarea
              ref={reportRef}
              readOnly
              value={reportText}
              placeholder="The report will appear here."
              /* Compact until there is something to read. At a fixed 300px the
                * empty box was the largest thing in the modal and made the
                * panel look like dead space; it grows once a report exists. */
              className={`mt-3 w-full resize-y rounded border border-gray-300 bg-gray-50 p-3 font-mono text-sm leading-relaxed text-gray-600 outline-none transition-[height] focus:border-[#115e59] ${
                reportText ? 'h-[52vh] min-h-[380px]' : 'h-[120px]'
              }`}
            />
          </div>
        </div>
        </div>
        </div>
      )}

      {/* ---------------- booking link ---------------- */}
      <section className="mt-6 rounded-lg border border-[#e5e7eb] bg-white p-5">
        <h2 className="text-sm font-semibold text-[#111827]">Mediator booking link</h2>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="relative">
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              className="flex min-w-[240px] items-center justify-between gap-3 rounded-md border border-[#d1d5db] bg-white px-3 py-2 text-sm text-[#333333] hover:border-[#115e59]"
            >
              <span className="truncate">{selectedMediator ? selectedMediator.name : 'Select Mediator'}</span>
              <svg className="h-4 w-4 shrink-0 text-[#666666]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {pickerOpen && (
              <div className="absolute z-20 mt-1 max-h-[320px] w-[280px] overflow-auto rounded-md border border-[#e5e7eb] bg-white shadow-lg">
                <div className="sticky top-0 border-b border-[#f0f0f0] bg-white p-2">
                  <input
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search mediators…"
                    className="w-full rounded border border-[#e5e7eb] px-2 py-1.5 text-sm outline-none focus:border-[#115e59]"
                  />
                </div>
                {filtered.length === 0 && (
                  <p className="px-3 py-3 text-sm text-[#666666]">No mediators match.</p>
                )}
                {filtered.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => chooseMediator(m)}
                    className={`block w-full truncate px-3 py-2 text-left text-sm hover:bg-[#f7f7f7] ${
                      selectedMediator?.id === m.id ? 'bg-[#e8fdf5] font-medium text-[#115e59]' : 'text-[#333333]'
                    }`}
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {bookingUrl && (
            <>
              <code className="flex-1 truncate rounded-md bg-[#f7f7f7] px-3 py-2 text-xs text-[#333333]">{bookingUrl}</code>
              <button
                type="button"
                onClick={copyLink}
                className="rounded-md bg-[#115e59] px-4 py-2 text-sm font-medium text-white hover:bg-[#0d4a46]"
              >
                {copied ? 'Copied' : 'Copy link'}
              </button>
              <a
                href={bookingUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-md border border-[#d1d5db] px-4 py-2 text-sm text-[#333333] hover:border-[#115e59]"
              >
                Open
              </a>
            </>
          )}
        </div>

        {/* Two genuinely different causes, so they get two different messages.
          * The old copy blamed the mediator's account for both, which sent
          * whoever hit it looking at the wrong thing entirely. */}
        {!bookingUrl && selectedMediator && (
          <p className="mt-3 text-sm text-amber-600">
            {selectedMediator.userId
              ? 'No booking link could be built — the Scale portal address is not configured for this deployment.'
              : 'No booking link could be built for this mediator — their account has no portal user id yet.'}
          </p>
        )}

        {/* The booking page itself, exactly as a party sees it.
          *
          * /embed/:mediator is registered under publicRouteWithoutLayout in the
          * mediator portal, so it renders standalone with no login and no
          * chrome — which is what makes it safe to frame here. Showing it beats
          * describing it: the case manager can check the rates, the open dates
          * and the mediator's name are right before sending the link to a firm,
          * without leaving the portal or signing in as anyone. */}
        {bookingUrl && (
          <div className="mt-5">
            {/* Label removed at Azeem's request. Reload stays, right-aligned on
              * its own row so the frame below it is unchanged. */}
            <div className="mb-2 flex items-center justify-end">
              <button
                type="button"
                onClick={() => setPreviewNonce((n) => n + 1)}
                className="text-xs text-[#115e59] hover:underline"
              >
                Reload
              </button>
            </div>
            {/* Full-bleed band, pulled out to the section's edges with negative
              * margins that cancel its p-5.
              *
              * The booking page centres its own card inside a max-width, so its
              * content can never line up with the label and dropdown above it —
              * it sat inset and simply read as misaligned. Letting the frame run
              * edge to edge on a tinted ground turns that into a deliberate
              * viewport: the page is obviously being shown inside something,
              * and centred content is what you expect there. */}
            <div className="-mx-5 bg-[#f4f5f4] px-0 py-2">
              <iframe
                key={`${bookingUrl}-${previewNonce}`}
                src={bookingUrl}
                title={`Booking page for ${selectedMediator?.name || 'mediator'}`}
                className="block h-[900px] w-full border-0 bg-transparent"
                loading="lazy"
              />
            </div>
            <p className="mt-3 text-xs text-[#999999]">
              This is the real booking page. Anything booked here creates a real case.
            </p>
          </div>
        )}
      </section>

    </div>
  );
};

export default BookingLink;
